import { forwardRef, useImperativeHandle, useState } from "react";
import { Button, InlineAlert } from "../../components/BaseComponents";
import SimpleSortTable, { TableColumn } from "../../components/BaseComponents/SimpleSortTable/SimpleSortTable";
import ConfirmationModal from "../../components/BaseComponents/ConfirmationModal";
import ResendInviteModal from "../../components/BaseComponents/ResendInviteModal/ResendInviteModal";
import Pagination from "../../components/BaseComponents/Pagination/Pagination";
import useApi from "../../hooks/useApi";
import usePaginatedFetch from "../../hooks/usePaginatedFetch";
import { formatDate, getApiErrorMessage, getCSRFToken } from "../../lib/utils";
import { PaginationMeta } from "../../types/PaginationMeta";
import AddTrainingUserModal from "./AddTrainingUserModal";
import EditTrainingUserModal from "./EditTrainingUserModal";
import ManageTrainingOrganisationsModal from "./ManageTrainingOrganisationsModal";
import UploadTrainingUsersModal from "./UploadTrainingUsersModal";

// Picks up a trainee's pending -> active change without a manual reload; no SSE exists in the app.
const POLL_INTERVAL_MS = 15000;

type TrainingSessionUsersResponse = {
    users: any[];
    meta: PaginationMeta;
};

type AdminTrainingSessionUsersProps = {
    trainingSessionId: string;
    sessionData?: any;
    userNameFilter?: string;
    organisationNameFilter?: string;
    onDataRefresh?: () => void;
};

function AdminTrainingSessionUsers({ 
    trainingSessionId,
    sessionData,
    userNameFilter = '',
    organisationNameFilter = '',
    onDataRefresh,
}: AdminTrainingSessionUsersProps, ref: any) {
    const api = useApi();
    // Issue #884: `error` (already correctly tracked by usePaginatedFetch) was previously never
    // read here, so a rejecting list GET (including the now-rejecting
    // READ_ONLY_ACCESS/ACCESS_AUDIT_UNAVAILABLE codes - #857) silently left the table looking
    // like a genuinely empty list instead of a failed load.
    const { rows: results, loading, error, pagination, onPageChange, refetch: onSearch } = usePaginatedFetch<TrainingSessionUsersResponse, any>({
        path: `/admin/training-sessions/${trainingSessionId}/users`,
        query: { userName: userNameFilter, organisationName: organisationNameFilter },
        getRows: (response) => response.users,
        pollIntervalMs: POLL_INTERVAL_MS,
    });

    const [addModalOpen, setAddModalOpen] = useState(false);
    const [editCandidate, setEditCandidate] = useState<any>(null);
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [manageOrganisationsModalOpen, setManageOrganisationsModalOpen] = useState(false);
    const [resendCandidate, setResendCandidate] = useState<any>(null);
    // #873: `resendLoading`/`resendError`/`resendResult` are owned here (not inside the dumb
    // ResendInviteModal) so a provider/transport failure never surfaces as an unhandled rejection,
    // and a successful rotation can show the *new* signup link instead of immediately closing.
    const [resendLoading, setResendLoading] = useState(false);
    const [resendError, setResendError] = useState<string | null>(null);
    const [resendResult, setResendResult] = useState<{ signupUrl: string, delivery: 'sent' | 'unconfirmed' } | null>(null);
    const [removeCandidate, setRemoveCandidate] = useState<any>(null);
    const [removeError, setRemoveError] = useState<string | null>(null);

    // Expose modal functions to parent via ref
    useImperativeHandle(ref, () => ({
        openAddModal: () => setAddModalOpen(true),
        openUploadModal: () => setUploadModalOpen(true),
        openManageOrganisationsModal: () => setManageOrganisationsModalOpen(true),
    }), []);

    const closeResendModal = () => {
        setResendCandidate(null);
        setResendError(null);
        setResendResult(null);
    }

    const onResend = async () => {
        if (!resendCandidate) {
            return;
        }
        setResendLoading(true);
        setResendError(null);
        try {
            const body = await api.post(`/admin/training-sessions/${trainingSessionId}/users/${resendCandidate.id}/resend-invite`, {
                _csrf: await getCSRFToken(),
            })
            // #873: the invitation was already rotated server-side by the time this resolves -
            // show the new link rather than closing the modal out from under the admin.
            setResendResult({ signupUrl: body.data.invite.signupUrl, delivery: body.data.delivery });
            onSearch();
            onDataRefresh?.();
        } catch (err) {
            // #873: the server may have rotated the invitation before the response was lost.
            // Refresh the list and require the admin to close this message before another attempt.
            setResendError('We could not confirm the resend outcome. The invitation may have changed; check the refreshed list before trying again.');
            onSearch();
            onDataRefresh?.();
        } finally {
            setResendLoading(false);
        }
    }

    const closeRemoveModal = () => {
        setRemoveCandidate(null);
        setRemoveError(null);
    }

    const onRemove = async () => {
        if (!removeCandidate) {
            return;
        }
        setRemoveError(null);
        try {
            await api.delete(`/admin/training-sessions/${trainingSessionId}/users/${removeCandidate.id}`, {
                _csrf: await getCSRFToken(),
            })
            setRemoveCandidate(null);
            onSearch();
            onDataRefresh?.();
        } catch (err) {
            // #873: never silently swallowed (no unhandled rejection) and the candidate is kept
            // so the admin sees the failure and can retry or cancel, rather than it vanishing.
            setRemoveError(getApiErrorMessage(err, 'This user could not be removed. Please try again.'));
        }
    }

    const dateRenderer = (date: string | null) => {
        if (!date) {
            return <span>-</span>;
        }
        return <span title={date}>{ formatDate(date) }</span>
    }

    const columns: TableColumn[] = [{
        id: 'fullName',
        label: 'Name',
        renderer: (key, row) => <span>{ row[key] }{ row.type === 'invitation' ? <span className="text-charcoal-300"> (pending)</span> : '' }</span>,
    }, {
        id: 'email',
        label: 'Email',
    }, {
        id: 'organisationName',
        label: 'Organisation',
        // Sub-task I-extra2 §4.3/§8.2/§16.3: jurisdiction appended to the org name, e.g.
        // "Acme Training (DE)", matching the same treatment on the "Manage organisations" list.
        renderer: (key, row) => <span>{row[key]}{row.jurisdictionCode ? <span className="text-charcoal-300"> ({row.jurisdictionCode})</span> : ''}</span>,
    }, {
        id: 'dateAdded',
        label: 'Date Added',
        renderer: (key, row) => dateRenderer(row[key]),
    }, {
        id: 'inviteSentAt',
        label: 'Invite Sent',
        renderer: (key, row) => dateRenderer(row[key]),
    }, {
        id: 'requestedUpgrade',
        label: 'Requested Upgrade',
        renderer: (key, row) => <span>{ row[key] ? 'Requested' : '' }</span>,
    }, {
        id: 'actions',
        label: 'Actions',
        headerAlign: 'center',
        textAlign: 'right',
        renderer: (key, row) => (
            <div className="space-x-2">
                {row.type === 'invitation' ? (
                    <Button
                        type="Outlined"
                        size="sm"
                        icon="fa-solid fa-paper-plane"
                        text="Resend invite"
                        hasLeadingIcon
                        isTableRowButton
                        onClick={() => setResendCandidate(row)}
                    />
                ) : null}
                <Button
                    type="Primary"
                    icon="fa-solid fa-pen-to-square"
                    hasLeadingIcon
                    size="sm"
                    isTableRowButton
                    onClick={() => setEditCandidate(row)}
                    accessibilityLabel={`Edit ${row.fullName}`}
                />
                <Button
                    type="Primary"
                    icon="fa-solid fa-trash"
                    hasLeadingIcon
                    size="sm"
                    isTableRowButton
                    onClick={() => setRemoveCandidate(row)}
                    accessibilityLabel={`Delete ${row.fullName}`}
                />
            </div>
        ),
    }]

    return (
        <div>
            {/* #884: wrapped with the next sibling in a single fragment child to
                avoid a false-positive TS2746 overload-resolution error when the
                root element's direct child count crosses this TS/React types
                version's inference threshold (verified empirically; no behavior
                change - renders identically to two separate top-level children). */}
            <>
                { error && (
                    <InlineAlert type='error' className='mb-4'>
                        <p>{ getApiErrorMessage(error, 'Failed to load training session users. Please try again.') }</p>
                        <Button
                            type='Primary'
                            hasTrailingIcon={true}
                            icon='fa-solid fa-rotate-right'
                            text='Try again'
                            additionalClass='mt-4'
                            onClick={() => onSearch()}
                        />
                    </InlineAlert>
                )}
                <SimpleSortTable
                    columns={columns}
                    data={results}
                    isLoading={loading}
                />
            </>
            <Pagination
                onPageChange={onPageChange}
                pagination={pagination}
                isLoading={loading}
            />
            <AddTrainingUserModal
                isOpen={addModalOpen}
                trainingSessionId={trainingSessionId}
                sessionData={sessionData}
                onClose={() => setAddModalOpen(false)}
                onAdded={() => {
                    setAddModalOpen(false);
                    onSearch();
                    onDataRefresh?.();
                }}
            />
            <EditTrainingUserModal
                isOpen={!!editCandidate}
                trainingSessionId={trainingSessionId}
                sessionData={sessionData}
                user={editCandidate}
                onClose={() => setEditCandidate(null)}
                onUpdated={() => {
                    setEditCandidate(null);
                    onSearch();
                    onDataRefresh?.();
                }}
            />
            <UploadTrainingUsersModal
                isOpen={uploadModalOpen}
                trainingSessionId={trainingSessionId}
                onClose={() => setUploadModalOpen(false)}
                onUploaded={() => {
                    setUploadModalOpen(false);
                    onSearch();
                    onDataRefresh?.();
                }}
                onUploadedWithIssues={() => {
                    onSearch();
                    onDataRefresh?.();
                }}
            />
            <ManageTrainingOrganisationsModal
                isOpen={manageOrganisationsModalOpen}
                trainingSessionId={trainingSessionId}
                onClose={() => {
                    setManageOrganisationsModalOpen(false);
                    onSearch();
                    onDataRefresh?.();
                }}
            />
            <ResendInviteModal
                open={!!resendCandidate}
                onCancel={closeResendModal}
                onConfirm={onResend}
                fullName={resendCandidate?.fullName}
                email={resendCandidate?.email}
                signupUrl={resendCandidate?.signupUrl}
                loading={resendLoading}
                error={resendError}
                result={resendResult}
            />
            <ConfirmationModal
                open={!!removeCandidate}
                onCancel={closeRemoveModal}
                onConfirm={onRemove}
                requireTypedConfirmation="delete"
                title="Remove user from session?"
            >
                { removeCandidate && (
                    <div>
                        <p className="text-white mt-8">
                            This will immediately remove {removeCandidate.fullName} ({removeCandidate.email}) from this
                            training session.
                        </p>
                        <p className="text-charcoal-100 mt-4">
                            Their account is kept and they can still log in - this never deletes or
                            locks an account. If this was the last remaining member of their training organisation, that
                            organisation is deleted if empty of other activity, or otherwise locked (for example if it
                            still holds an open invitation) rather than silently left unattended. Shared organisations
                            with other members are never affected. There is no undo.
                        </p>
                        { removeError && (
                            <InlineAlert type='error' className='mt-4'>{ removeError }</InlineAlert>
                        )}
                    </div>
                )}
            </ConfirmationModal>
        </div>
    )
}

export default forwardRef(AdminTrainingSessionUsers);
